import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiMapPin, FiSun, FiWind } from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const LocationSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 70%",
        toggleActions: "play none none none",
      },
    });

    tl.from(section.querySelectorAll(".content-animate"), {
      opacity: 0,
      y: 50,
      stagger: 0.2,
      duration: 0.8,
      ease: "power2.out",
    }).from(
      mapRef.current,
      {
        opacity: 0,
        scale: 0.95,
        duration: 1,
        ease: "power3.out",
      },
      "-=0.5"
    );

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const locationFeatures = [
    {
      icon: <FiSun className="h-7 w-7 text-amber-500" />,
      title: "Sun-Kissed Plains",
      description:
        "Bathed in golden sunlight, our lands are fertile and full of life.",
    },
    {
      icon: <FiWind className="text-sky-500 h-7 w-7" />,
      title: "Gentle Breezes",
      description:
        "A constant, soothing breeze whispers tales of old through the trees.",
    },
  ];

  return (
    <section ref={sectionRef} className="bg-stone-50 py-20 md:py-28">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 md:gap-16">
        {/* Left Side: Content */}
        <div className="order-2 md:order-1">
          <div className="content-animate text-accent flex items-center gap-3 font-semibold">
            <FiMapPin />
            <span>Our Home</span>
          </div>
          <h2 className="content-animate mt-4 font-serif text-4xl font-semibold leading-tight text-nature-green-dark md:text-5xl">
            Nunya, Isuikwuato LGA, Abia State, Nigeria
          </h2>
          <p className="content-animate mt-6 text-lg leading-relaxed text-gray-700">
            Nestled in a gentle curve of the great river, Nunya is a sanctuary
            of peace in Isuikwuato LGA, Abia State, Nigeria. Surrounded by
            rolling hills and ancient forests, this land has provided for us for
            generations, its rich soil and clean waters a testament to the
            harmony between our people and nature.
          </p>
          <div className="content-animate mt-10 space-y-6">
            {locationFeatures.map((feature, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="rounded-full border border-stone-200/60 bg-white p-3 shadow-sm">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {feature.title}
                  </h3>
                  <p className="mt-1 text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Map */}
        <div
          ref={mapRef}
          className="order-1 h-80 min-h-[24rem] overflow-hidden rounded-2xl border-4 border-white shadow-lg md:order-2 md:h-full"
        >
          <div
            className="h-full w-full bg-gray-400"
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9927.33096086288!2d7.412945677340552!3d5.695417474139698!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x10431fe2130c1e35%3A0xd8086beeb9ad0252!2sNunya%2C%20Isuikwuato%2C%20Abia%20State%2C%20Nigeria!5e0!3m2!1sen!2sus!4v1767406133264!5m2!1sen!2sus" width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`,
            }}
          />
        </div>
      </div>
      <div className="from-transparent to-transparent mt-28 h-px w-full bg-gradient-to-r via-amber-300"></div>
    </section>
  );
};

export default LocationSection;
